import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ButtonGroup, Typography, useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import L from 'leaflet'
import { Marker } from 'react-leaflet'

import LeafletMap from 'common/LeafletMap'
import { SavedBaseTileLayer } from 'Map/BaseTileLayers'
import { ToggleButton } from 'ToggleButton'
import { strings } from 'strings'

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
function toPersianDigits(n: number | string): string {
  return String(n).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)])
}

export interface MapListColumn<T> {
  label: string
  ltr?: boolean
  render: (row: T) => ReactNode
  // When provided, the header becomes clickable and rows are sorted by this
  // value. Numbers sort numerically; strings sort lexicographically. Returning
  // null/undefined places the row at the end.
  sortValue?: (row: T) => number | string | null | undefined
  // Initial direction when the column is first activated. Defaults to 'desc'
  // for numeric metrics, 'asc' otherwise — callers can pin it explicitly.
  defaultSortDir?: 'asc' | 'desc'
}

export interface MapListViewProps<T> {
  rows: T[]
  columns: MapListColumn<T>[]
  rowKey: (row: T) => string | number
  getLocation: (row: T) => [number, number] | null
  toolbar?: ReactNode
  hint?: ReactNode
  markerColor?: string
  selectedColor?: string
  focusZoom?: number
}

type RowKey = string | number

export function MapListView<T>(props: MapListViewProps<T>) {
  const {
    rows,
    columns,
    rowKey,
    getLocation,
    toolbar,
    hint,
    markerColor = '#1976d2',
    selectedColor = '#c62828',
    focusZoom = 10,
  } = props

  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [tab, setTab] = useState<'map' | 'list'>('list')
  const [selected, setSelected] = useState<RowKey | null>(null)
  const [sort, setSort] = useState<{
    columnIndex: number
    dir: 'asc' | 'desc'
  } | null>(null)

  const sortedRows = useMemo(() => {
    if (!sort) return rows
    const col = columns[sort.columnIndex]
    if (!col?.sortValue) return rows
    const sign = sort.dir === 'asc' ? 1 : -1
    const valueOf = col.sortValue
    const decorated = rows.map((row, i) => ({
      row,
      i,
      v: valueOf(row),
    }))
    decorated.sort((a, b) => {
      const av = a.v
      const bv = b.v
      const aMissing = av === null || av === undefined
      const bMissing = bv === null || bv === undefined
      // Always push missing values to the bottom regardless of direction so
      // that toggling asc/desc doesn't surface "—" rows above real data.
      if (aMissing && bMissing) return a.i - b.i
      if (aMissing) return 1
      if (bMissing) return -1
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * sign
      }
      return String(av).localeCompare(String(bv)) * sign
    })
    return decorated.map((d) => d.row)
  }, [rows, columns, sort])

  const onSortClick = (columnIndex: number) => {
    const col = columns[columnIndex]
    if (!col?.sortValue) return
    setSort((prev) => {
      if (!prev || prev.columnIndex !== columnIndex) {
        return { columnIndex, dir: col.defaultSortDir ?? 'desc' }
      }
      return { columnIndex, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    })
  }
  const mapRef = useRef<L.Map | null>(null)
  const rowRefs = useRef<Map<RowKey, HTMLElement | null>>(new Map())
  const fittedSignature = useRef<string>('')
  const pendingAction = useRef<
    { kind: 'fly'; key: RowKey } | { kind: 'scroll'; key: RowKey } | null
  >(null)

  const markers = useMemo(() => {
    const out: { key: RowKey; lat: number; lng: number; row: T }[] = []
    for (const r of rows) {
      const loc = getLocation(r)
      if (!loc) continue
      const [lat, lng] = loc
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue
      if (lat === 0 && lng === 0) continue
      out.push({ key: rowKey(r), lat, lng, row: r })
    }
    return out
  }, [rows, getLocation, rowKey])

  useEffect(() => {
    if (!mapRef.current || markers.length === 0) return
    const signature = markers
      .map((m) => String(m.key))
      .sort()
      .join('|')
    if (fittedSignature.current === signature) return
    fittedSignature.current = signature
    const map = mapRef.current
    const latlngs = markers.map((m) => L.latLng(m.lat, m.lng))
    requestAnimationFrame(() => {
      map.invalidateSize()
      if (latlngs.length === 1) {
        map.setView(latlngs[0], Math.min(focusZoom - 1, 8))
      } else {
        map.fitBounds(L.latLngBounds(latlngs), {
          padding: [24, 24],
          maxZoom: focusZoom - 1,
        })
      }
    })
  }, [markers, focusZoom])

  const focusRow = useCallback(
    (key: RowKey) => {
      const m = markers.find((x) => x.key === key)
      if (!m || !mapRef.current) return
      mapRef.current.flyTo([m.lat, m.lng], focusZoom, { duration: 0.8 })
    },
    [markers, focusZoom],
  )

  const scrollRowIntoView = useCallback((key: RowKey) => {
    const el = rowRefs.current.get(key)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  useEffect(() => {
    if (!mapRef.current) return
    const map = mapRef.current
    const raf = requestAnimationFrame(() => {
      map.invalidateSize()
      const action = pendingAction.current
      if (!action) return
      pendingAction.current = null
      if (action.kind === 'fly' && tab === 'map') {
        focusRow(action.key)
      } else if (action.kind === 'scroll' && tab === 'list') {
        scrollRowIntoView(action.key)
      }
    })
    return () => cancelAnimationFrame(raf)
  }, [isMobile, tab, focusRow, scrollRowIntoView])

  const handleRowClick = (key: RowKey) => {
    setSelected(key)
    if (isMobile) {
      pendingAction.current = { kind: 'fly', key }
      setTab('map')
    } else {
      focusRow(key)
    }
  }

  const handleMarkerClick = (key: RowKey) => {
    setSelected(key)
    if (isMobile) {
      pendingAction.current = { kind: 'scroll', key }
      setTab('list')
    } else {
      scrollRowIntoView(key)
    }
  }

  const buildIcon = (highlighted: boolean) => {
    const size = highlighted ? 20 : 16
    const color = highlighted ? selectedColor : markerColor
    const half = size / 2
    const shadow = highlighted
      ? '0 0 0 4px rgba(198,40,40,0.25), 0 0 0 1px #fff inset'
      : '0 0 0 2px #fff, 0 1px 3px rgba(0,0,0,0.4)'
    return L.divIcon({
      className: 'maplist-marker',
      html: `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;box-shadow:${shadow};"></div>`,
      iconSize: [size, size],
      iconAnchor: [half, half],
    })
  }

  const mapPanel = (
    <div className={classes.mapPanel}>
      <LeafletMap ref={mapRef as any}>
        <SavedBaseTileLayer />
        {markers.map((m) => {
          const highlighted = selected === m.key
          return (
            <Marker
              key={String(m.key)}
              position={[m.lat, m.lng]}
              icon={buildIcon(highlighted)}
              zIndexOffset={highlighted ? 1000 : 0}
              eventHandlers={{
                click: () => handleMarkerClick(m.key),
              }}
            />
          )
        })}
      </LeafletMap>
      {markers.length === 0 && (
        <div className={classes.mapEmpty}>
          <Typography variant="body2">{strings.noMapLocations}</Typography>
        </div>
      )}
    </div>
  )

  const listPanel = (
    <div className={classes.listPanel}>
      {toolbar && <div className={classes.toolbar}>{toolbar}</div>}
      {hint && <div className={classes.hint}>{hint}</div>}
      <div className={classes.listScroll}>
        {isMobile ? (
          <div className={classes.cardList}>
            {sortedRows.map((row, i) => {
              const key = rowKey(row)
              const active = selected === key
              return (
                <div
                  key={key}
                  ref={(el) => {
                    rowRefs.current.set(key, el)
                  }}
                  className={
                    active
                      ? `${classes.card} ${classes.cardActive}`
                      : classes.card
                  }
                  onClick={() => handleRowClick(key)}
                >
                  <div className={classes.cardHeader}>
                    <span className={classes.cardRowNum}>
                      {strings.row} {toPersianDigits(i + 1)}
                    </span>
                  </div>
                  {columns.map((col) => (
                    <div className={classes.cardRow} key={col.label}>
                      <span className={classes.cardLabel}>{col.label}</span>
                      <span
                        className={
                          col.ltr
                            ? `${classes.cardValue} ${classes.ltr}`
                            : classes.cardValue
                        }
                      >
                        {col.render(row)}
                      </span>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        ) : (
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.rowNumCell}>{strings.row}</th>
                {columns.map((col, ci) => {
                  const isSortable = !!col.sortValue
                  const isActive = sort?.columnIndex === ci
                  return (
                    <th
                      key={col.label}
                      className={
                        isSortable
                          ? `${classes.sortableHeader}${
                              isActive ? ` ${classes.sortableHeaderActive}` : ''
                            }`
                          : undefined
                      }
                      onClick={isSortable ? () => onSortClick(ci) : undefined}
                    >
                      <span className={classes.sortHeaderInner}>
                        {col.label}
                        {isSortable && (
                          <span className={classes.sortIndicator}>
                            {isActive
                              ? sort?.dir === 'asc'
                                ? '▲'
                                : '▼'
                              : '⇅'}
                          </span>
                        )}
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row, i) => {
                const key = rowKey(row)
                const active = selected === key
                return (
                  <tr
                    key={key}
                    ref={(el) => {
                      rowRefs.current.set(key, el)
                    }}
                    className={active ? classes.rowActive : undefined}
                    onClick={() => handleRowClick(key)}
                  >
                    <td className={classes.rowNumCell}>
                      {toPersianDigits(i + 1)}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col.label}
                        className={col.ltr ? classes.ltr : undefined}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <div className={classes.mobileWrap}>
        <div className={classes.tabs}>
          <ButtonGroup disableElevation variant="outlined" size="small">
            <ToggleButton
              selected={tab === 'list'}
              onClick={() => setTab('list')}
            >
              {strings.viewList}
            </ToggleButton>
            <ToggleButton
              selected={tab === 'map'}
              onClick={() => setTab('map')}
            >
              {strings.viewMap}
            </ToggleButton>
          </ButtonGroup>
        </div>
        <div className={classes.mobileBody}>
          <div
            className={
              tab === 'map'
                ? `${classes.mobilePane} ${classes.mobilePaneActive}`
                : classes.mobilePane
            }
          >
            {mapPanel}
          </div>
          <div
            className={
              tab === 'list'
                ? `${classes.mobilePane} ${classes.mobilePaneActive}`
                : classes.mobilePane
            }
          >
            {listPanel}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.desktopWrap}>
      {listPanel}
      {mapPanel}
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  desktopWrap: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    width: '100%',
    height: '100%',
    minHeight: 0,
  },
  mobileWrap: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    gap: theme.spacing(1),
  },
  tabs: {
    display: 'flex',
    justifyContent: 'center',
    flexShrink: 0,
  },
  mobileBody: {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  },
  mobilePane: {
    position: 'absolute',
    inset: 0,
    display: 'none',
  },
  mobilePaneActive: {
    display: 'flex',
    flexDirection: 'column',
  },
  mapPanel: {
    position: 'relative',
    flex: '1 1 55%',
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    height: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
  },
  mapEmpty: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    background: 'rgba(255,255,255,0.9)',
    padding: theme.spacing(0.75, 1),
    borderRadius: 4,
    border: '1px solid #ddd',
    zIndex: 500,
  },
  listPanel: {
    flex: '1 1 45%',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    height: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(0.5, 0.75),
    borderBottom: '1px solid #eee',
    background: '#fafafa',
    flexShrink: 0,
  },
  hint: {
    fontSize: '0.8rem',
    color: '#555',
    background: '#f5f5f5',
    padding: theme.spacing(0.75, 1),
    borderBottom: '1px solid #eee',
    lineHeight: 1.6,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.72rem',
      lineHeight: 1.5,
    },
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th, & td': {
      flip: false,
      padding: theme.spacing(0.75, 1),
      textAlign: 'right',
      borderBottom: '1px solid #eee',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
    },
    '& th': {
      flip: false,
      fontWeight: 600,
      background: '#fafafa',
      textAlign: 'right',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    '& tbody tr': {
      cursor: 'pointer',
    },
    '& tbody tr:hover': {
      background: '#f5faff',
    },
  },
  rowActive: {
    background: '#fff3e0 !important',
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: '#f0f4ff',
    },
  },
  sortableHeaderActive: {
    background: '#e3eaff',
  },
  sortHeaderInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  sortIndicator: {
    fontSize: '0.7rem',
    color: '#999',
  },
  rowNumCell: {
    width: 44,
    color: '#888',
    [theme.breakpoints.down('xs')]: {
      width: 32,
    },
  },
  ltr: {
    direction: 'ltr',
    textAlign: 'left',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  card: {
    border: '1px solid #eee',
    borderRadius: 6,
    padding: theme.spacing(1),
    background: '#fff',
    cursor: 'pointer',
  },
  cardActive: {
    borderColor: '#c62828',
    background: '#fff3e0',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    borderBottom: '1px solid #f0f0f0',
  },
  cardRowNum: {
    fontSize: '0.78rem',
    color: '#888',
    fontWeight: 600,
  },
  cardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(0.25, 0),
    fontSize: '0.82rem',
  },
  cardLabel: {
    color: '#666',
    fontWeight: 500,
    flexShrink: 0,
  },
  cardValue: {
    color: '#222',
    textAlign: 'left',
    wordBreak: 'break-word',
    minWidth: 0,
  },
}))
